import React, { useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import moment from "moment";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
// the Brisk dialogs' frame and the Company Wallet dialog look, so these match them
import { CallRequestUrl, DialogFrame } from "../../Policy/BriskCertificateList/BriskV2Dialogs";
import { parseFieldChanges } from "../../Policy/BriskCertificateList/briskV2ListUtils";
import Styles from "../../ClientCompanyWallet/ClientCompanyWallet.module.scss";
import { cancelRoadzenCertificate, getRoadzenCertDownloadUrl, getRoadzenCertificate } from "../../../Service/RoadzenService";
import { callLabel, callOutcome, cancelRefundLine, historyStep, isSafeLink, rupees, statusColor, whenText } from "./roadzenCertUtils";

const buttonSx = { textTransform: "none", borderRadius: "6px" };
const sectionTitleSx = { fontSize: 13.5, fontWeight: 700, color: "#1E3A5F", mt: 2.5, mb: 1.25 };
const STEP_COLOURS = { navy: "#1E3A5F", green: "#12B76A", red: "#F04438", amber: "#F79009", grey: "#98A2B3" };
const STEP_ICONS = { sent: SendRoundedIcon, done: CheckRoundedIcon, failed: ClearRoundedIcon, cancel: BlockRoundedIcon, warning: PriorityHighRoundedIcon, money: CurrencyRupeeRoundedIcon, edit: EditRoundedIcon, note: NotesRoundedIcon, recheck: RefreshRoundedIcon };
const LABELS = {
  proposer_name: "Customer Name",
  proposer_mobile: "Mobile",
  proposer_email: "Email",
  proposer_dob: "Date of Birth",
  proposer_gender: "Gender",
  address_line1: "Address Line 1",
  address_line2: "Address Line 2",
  proposer_city: "City",
  proposer_state: "State",
  proposer_pincode: "Pincode",
  nominee_name: "Nominee Name",
  nominee_relationship: "Nominee Relationship",
  nominee_gender: "Nominee Gender",
  nominee_dob: "Nominee Date of Birth",
  nominee_is_legal_heir: "Nominee is Legal Heir",
  vehicle_make: "Make",
  vehicle_model: "Model",
  fuel_type: "Fuel Type",
};

/** Cancel at RoadZen (admin only). Says what comes back to the Company Wallet before it is pressed. */
export const CancelCertificateDialog = ({ row, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    const res = await cancelRoadzenCertificate(row.id, reason.trim());
    setBusy(false);
    if (res.error) {
      ToastError(res.message);
      return;
    }
    ToastSuccess(res.message);
    onDone();
  };
  return (
    <DialogFrame
      title="Cancel Certificate"
      subtitle={`${row.certificateNumber} · ${row.customerName}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="outlined" sx={buttonSx} onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button variant="outlined" className={Styles.RemoveButton} onClick={send} disabled={busy || reason.trim().length < 3}>
            {busy ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Cancelling…
              </>
            ) : (
              "Cancel Certificate"
            )}
          </Button>
        </>
      }
    >
      <Box className={Styles.PanelBalanceRow} sx={{ mb: 2 }}>
        {cancelRefundLine(row)}
      </Box>
      <Typography component="label" htmlFor="roadzen-cancel-reason" className={Styles.FieldLabel}>
        Reason<span style={{ color: "#B42318" }}> *</span>
      </Typography>
      <input
        id="roadzen-cancel-reason"
        className={Styles.InputFiled}
        maxLength={500}
        placeholder="Why is it cancelled?"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={busy}
      />
      <Typography sx={{ fontSize: 11, color: "#667085", mt: "3px" }}>3 to 500 letters, sent to RoadZen. RoadZen allows a cancel within 50 days.</Typography>
    </DialogFrame>
  );
};

const Pairs = ({ items }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, columnGap: 3, rowGap: 1.25 }}>
    {items
      .filter(([, v]) => v !== null && v !== undefined && v !== "" && v !== "-")
      .map(([label, value]) => (
        <Box key={label} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11.5, color: "#667085", fontWeight: 600 }}>{label}</Typography>
          <Typography sx={{ fontSize: 13.5, color: "#101828", overflowWrap: "anywhere" }}>{value}</Typography>
        </Box>
      ))}
  </Box>
);

const FieldChanges = ({ text }) => {
  const parsed = parseFieldChanges(text);
  if (!parsed) return <Typography sx={{ fontSize: 12.5, color: "#475467" }}>{text}</Typography>;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", columnGap: 1.5, rowGap: 0.5, mt: 0.5 }}>
      {parsed.changes.map((c) => (
        <React.Fragment key={c.field}>
          <Typography sx={{ fontSize: 12.5, color: "#667085" }}>{LABELS[c.field] || c.label}</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#101828", overflowWrap: "anywhere" }}>
            <span style={{ textDecoration: "line-through", color: "#98A2B3" }}>{c.from}</span> → <b>{c.to}</b>
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
};

const Timeline = ({ history }) => {
  if (!history.length) return <Typography sx={{ fontSize: 12.5, color: "#667085" }}>Nothing recorded yet</Typography>;
  return (
    <Box data-testid="roadzen-cert-timeline">
      {history.map((entry, i) => {
        const step = historyStep(entry);
        const Icon = STEP_ICONS[step.icon] || NotesRoundedIcon;
        return (
          <Box
            key={i}
            sx={{
              position: "relative",
              display: "grid",
              gridTemplateColumns: "26px minmax(0, 1fr)",
              columnGap: 1.25,
              pb: 1.5,
              "&:last-of-type": { pb: 0 },
              "&:not(:last-of-type)::after": { content: '""', position: "absolute", left: "12px", top: "28px", bottom: "2px", width: "2px", backgroundColor: "#E4E7EC" },
            }}
          >
            <Box sx={{ width: 26, height: 26, borderRadius: "50%", backgroundColor: STEP_COLOURS[step.tone], color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon sx={{ fontSize: 15 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", columnGap: 1.5, minHeight: 26 }}>
                <Typography component="div" sx={{ fontSize: 13.5, fontWeight: 700, color: STEP_COLOURS[step.tone] }}>
                  {step.title}
                </Typography>
                <Typography component="div" sx={{ fontSize: 12, color: "#667085", whiteSpace: "nowrap" }}>
                  {whenText(entry.at)}
                  {entry.byName ? ` · by ${entry.byName}` : ""}
                </Typography>
              </Box>
              {entry.kind === "fields" ? (
                <>
                  <FieldChanges text={entry.text} />
                  {entry.data && entry.data.reason && <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}>Reason: {entry.data.reason}</Typography>}
                </>
              ) : (
                <Typography component="div" sx={{ fontSize: 12.5, color: "#475467", overflowWrap: "anywhere" }}>
                  {step.icon === "recheck" ? String(entry.text || "").replace(/^Recheck\.\s*/, "") : entry.text}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const MONO = "Menlo, Consolas, monospace";
const pretty = (v) => (typeof v === "string" ? v : JSON.stringify(v === undefined ? null : v, null, 2));
const codeSx = { m: 0, p: 1.25, backgroundColor: "#F5F7FB", border: "1px solid #EAECF0", borderRadius: "6px", fontSize: 12, fontFamily: MONO, whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 260, overflow: "auto" };

/** Everything about ONE certificate (its own history, not the other pages'): summary, files, what happened, RoadZen's calls. */
export const HistoryDialog = ({ row, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!row) return undefined;
    let alive = true;
    setLoading(true);
    getRoadzenCertificate(row.id)
      .then((res) => {
        if (!alive) return;
        if (res.error) setError(res.message);
        else setDetail(res.data);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [row]);
  if (!row) return null;
  const r = { ...row, ...(detail || {}) };
  const open = async (params) => {
    const tab = window.open("", "_blank");
    const res = await getRoadzenCertDownloadUrl(row.id, params);
    if (res.error || !res.data || !isSafeLink(res.data.url)) {
      if (tab) tab.close();
      ToastError(res.message || "The file is not available right now");
      return;
    }
    if (tab) tab.location.href = res.data.url;
  };
  return (
    <DialogFrame
      title="History"
      subtitle={`${r.certificateNumber || r.customerName} · ${r.apiEnv}`}
      onClose={onClose}
      maxWidth={960}
      footer={
        <Button variant="outlined" sx={buttonSx} onClick={onClose}>
          Close
        </Button>
      }
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Box sx={{ px: 1.25, py: 0.25, borderRadius: "999px", fontSize: 12, fontWeight: 700, color: "#fff", backgroundColor: statusColor(r.status) === "green" ? "#12B76A" : statusColor(r.status) === "red" ? "#F04438" : statusColor(r.status) === "grey" ? "#98A2B3" : "#F79009" }}>
          {r.statusText || r.status}
        </Box>
        {r.charge && r.charge.state === "DEBITED" && (
          <Typography sx={{ fontSize: 12.5, color: "#475467" }}>
            {rupees(r.charge.amount)} charged to the Company Wallet "RoadZen"{r.refund && r.refund.state === "DONE" ? `, ${rupees(r.refund.amount)} refunded` : ""}
          </Typography>
        )}
      </Box>
      <Pairs
        items={[
          ["Certificate Number", r.certificateNumber],
          ["CPA Certificate", r.cpaCertificateNumber],
          ["Customer", r.customerName],
          ["Mobile", r.mobile],
          ["Product", r.product ? `${r.product.code} · ${r.product.name}` : ""],
          ["Total Premium", r.totalPremium ? rupees(r.totalPremium) : ""],
          ["Net / GST", r.netPremium ? `${rupees(r.netPremium)} / ${rupees(r.gst)}` : ""],
          ["Cover", r.riskStart ? `${r.riskStart} to ${r.riskEnd || "-"}` : ""],
          ["Chassis Number", r.chassisNumber],
          ["Registration Number", r.registrationNumber],
          ["Issued under", r.roadzenUser ? `${r.roadzenUser.name} (${r.roadzenUser.code})` : ""],
          ["Online Policy", r.source && r.source.policyNo],
          ["Created by", r.createdByName],
          ["Created", whenText(r.createdAt)],
        ]}
      />
      {(r.hasPdf || (r.insurerPdfs || []).length > 0) && (
        <>
          <Typography sx={sectionTitleSx}>Files (our saved copies)</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {r.hasPdf && (
              <Button size="small" variant="outlined" sx={buttonSx} onClick={() => open({})}>
                Certificate {r.certificateNumber}
              </Button>
            )}
            {(r.insurerPdfs || []).map((f) => (
              <Button key={f.index} size="small" variant="outlined" sx={buttonSx} onClick={() => open({ kind: "insurer", index: f.index })}>
                Policy PDF · {f.fileName}
              </Button>
            ))}
          </Box>
        </>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <Typography sx={sectionTitleSx}>What happened</Typography>
      {loading && <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} aria-label="Loading certificate history" />}
      {detail && <Timeline history={detail.history || []} />}
      {detail && <RoadzenCallsList calls={detail.calls || []} />}
    </DialogFrame>
  );
};

/**
 * Every call made to RoadZen for this certificate, folded away like the Brisk list: each call says what it was for in
 * plain words; opening it shows the exact call, what was sent and what RoadZen answered.
 */
const RoadzenCallsList = ({ calls }) => (
  <Accordion disableGutters elevation={0} data-testid="roadzen-calls" sx={{ mt: 2.5, border: "1px solid #EAECF0", borderRadius: "8px !important", "&:before": { display: "none" } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1, flexWrap: "wrap" } }}>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: STEP_COLOURS.navy }}>Details for support</Typography>
      <Typography sx={{ fontSize: 12, color: "#667085" }}>{calls.length === 1 ? "1 call to RoadZen" : `${calls.length} calls to RoadZen`}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>
      {!calls.length && <Typography sx={{ fontSize: 12.5, color: "#667085" }}>No call to RoadZen saved for this certificate</Typography>}
      {calls.map((c, i) => {
        const outcome = callOutcome(c);
        return (
          <Accordion key={i} disableGutters elevation={0} sx={{ border: "1px solid #EAECF0", borderRadius: "8px !important", mb: 1, "&:last-of-type": { mb: 0 }, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 42, "& .MuiAccordionSummary-content": { my: 0.5, alignItems: "center", gap: 1, flexWrap: "wrap" } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#101828" }}>{callLabel(c)}</Typography>
              <Chip size="small" label={outcome.label} color={outcome.ok ? "success" : "warning"} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              <Typography sx={{ fontSize: 12, color: "#667085", whiteSpace: "nowrap" }}>{moment(c.at).format("DD MMM YYYY, hh:mm:ss A")}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <CallRequestUrl call={c} fallback={c.path} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#344054", mb: 0.5 }}>What we sent</Typography>
                  <Box component="pre" sx={codeSx}>
                    {pretty(c.request)}
                  </Box>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#344054", mb: 0.5 }}>
                    What RoadZen answered{c.code ? ` (${c.code})` : ""}
                  </Typography>
                  <Box component="pre" sx={codeSx}>
                    {pretty(c.response)}
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </AccordionDetails>
  </Accordion>
);
